import React, { Suspense, useEffect, useState } from "react";
import AdminBreadcrumbs from "./shared/AdminBreadcrumbs";
import AdminSidebar from "./shared/AdminSidebar";
import AdminTopbar from "./shared/AdminTopbar";
import FlashMessage from "./shared/FlashMessage";
import SchoolBridgeLoader from "./shared/SchoolBridgeLoader";
import applicationName from "./shared/applicationName";
import t from "./shared/translate";
import buildLazyScreenResolver from "./buildLazyScreenResolver";
import { initAdminSentry, syncAdminSentryContext } from "../utils/sentry";

const adminScreenContext = require.context("./screens", true, /^\.\/Admin.*\.js$/, "lazy");
const resolveScreenComponent = buildLazyScreenResolver(adminScreenContext);

export default function AdminApp(props) {
  const { view, layout } = props;
  const resolvedApplicationName = props.applicationName || applicationName();
  const ScreenComponent = resolveScreenComponent(view);
  const isAdminView = typeof view === "string" && view.startsWith("Admin");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const initialFlashMessages = [
    props.alertMessage ? { message: props.alertMessage, mode: "error" } : null,
    props.noticeMessage ? { message: props.noticeMessage, mode: "notice" } : null
  ].filter(Boolean);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedState = window.localStorage.getItem("schoolBridge.adminSidebarCollapsed");
    if (storedState === null) {
      setSidebarCollapsed(window.innerWidth < 1024);
      return;
    }

    setSidebarCollapsed(storedState === "true");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem("schoolBridge.adminSidebarCollapsed", sidebarCollapsed ? "true" : "false");
  }, [sidebarCollapsed]);

  useEffect(() => {
    initAdminSentry(props.sentry);
  }, [props.sentry]);

  useEffect(() => {
    syncAdminSentryContext({
      currentUser: props.currentUser,
      workspaceSchool: props.workspaceSchool,
      view,
    });
  }, [props.currentUser, props.workspaceSchool, view]);

  if (ScreenComponent) {
    const renderedScreen = (
      <Suspense fallback={<div className="p-6 lg:p-8"><SchoolBridgeLoader /></div>}>
        <ScreenComponent {...props} />
      </Suspense>
    );

    if (layout === "auth") {
      return (
        <>
          <FlashMessage initialMessages={initialFlashMessages} />
          {renderedScreen}
        </>
      );
    }

    if (isAdminView) {
      return (
        <div className="min-h-screen bg-slate-100 text-slate-900">
          <FlashMessage initialMessages={initialFlashMessages} />
          <div className="flex min-h-screen flex-row">
            <AdminSidebar
              currentView={view}
              workspaceSchool={props.workspaceSchool}
              workspaceSection={props.workspaceSection}
              branding={props.branding}
              currentUser={props.currentUser}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed((current) => !current)}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <AdminTopbar
                currentUser={props.currentUser}
                workspaceSchool={props.workspaceSchool}
                currentLocale={props.locale}
                availableLocales={props.availableLocales}
              />
              <AdminBreadcrumbs items={props.breadcrumbs || []} />
              <div className="flex-1">
                {renderedScreen}
              </div>
              <footer className="px-5 pb-6 pt-2 text-right text-xs font-medium text-slate-400 lg:px-8">
                {t("app.footer", { application_name: resolvedApplicationName })}
              </footer>
            </div>
          </div>
        </div>
      );
    }
  }

  return <div>{SchoolBridge.translations.general.unknown_view} {view}</div>;
}
